"use client";
import { cn } from "../../lib/utils";
import { motion } from "framer-motion";

export const BentoGrid = ({ className, children }) => {
  return (
    <div
      className={cn(
        "grid md:auto-rows-[18rem] grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 max-w-full mx-auto",
        className
      )}
    >
      {children}
    </div>
  );
};

export const BentoGridItem = ({
  className,
  title,
  description,
  header,
  icon,
  value,
  trend,
  trendUp,
  onClick,
}) => {
  return (
    <motion.div
      whileHover={{ scale: 1.02, y: -2 }}
      transition={{ duration: 0.2 }}
      className={cn(
        "row-span-1 rounded-xl group/bento hover:shadow-xl transition duration-200 shadow-none p-4 bg-card border border-border justify-between flex flex-col space-y-4 cursor-pointer",
        className
      )}
      onClick={onClick}
    >
      {header && <div className="flex-shrink-0">{header}</div>}
      <div className="group-hover/bento:translate-x-1 transition duration-200">
        <div className="flex items-center gap-2 mb-2">
          {icon && (
            <div className="p-2 rounded-lg bg-muted text-muted-foreground group-hover/bento:text-primary transition-colors">
              {icon}
            </div>
          )}
          <div className="font-sans font-medium text-foreground text-sm">
            {title}
          </div>
        </div>
        {value !== undefined && (
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold text-foreground">{value}</span>
            {trend !== undefined && (
              <span
                className={cn(
                  "text-sm font-medium",
                  trendUp ? "text-success" : "text-destructive"
                )}
              >
                {trendUp ? "+" : ""}{trend}%
              </span>
            )}
          </div>
        )}
        {description && (
          <div className="font-sans font-normal text-muted-foreground text-xs mt-2">
            {description}
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Stat card variant for dashboard - uses glowing borders for modern aesthetic
export const StatCard = ({
  title,
  value,
  description,
  icon,
  trend,
  trendUp,
  trendLabel,
  color = "blue",
  onClick,
  className,
  glow = true, // Enable glow by default for modern look
}) => {
  // Glowing border styles (modern, lightweight)
  const glowStyles = {
    blue: {
      border: "border-info/30",
      glow: "shadow-[0_0_20px_-5px_hsl(var(--info)/0.2)] hover:shadow-[0_0_30px_-5px_hsl(var(--info)/0.35)] hover:border-info/50",
      icon: "text-info bg-info/10",
    },
    green: {
      border: "border-success/30",
      glow: "shadow-[0_0_20px_-5px_hsl(var(--success)/0.2)] hover:shadow-[0_0_30px_-5px_hsl(var(--success)/0.35)] hover:border-success/50",
      icon: "text-success bg-success/10",
    },
    red: {
      border: "border-destructive/30",
      glow: "shadow-[0_0_20px_-5px_hsl(var(--destructive)/0.2)] hover:shadow-[0_0_30px_-5px_hsl(var(--destructive)/0.35)] hover:border-destructive/50",
      icon: "text-destructive bg-destructive/10",
    },
    amber: {
      border: "border-warning/30",
      glow: "shadow-[0_0_20px_-5px_hsl(var(--warning)/0.2)] hover:shadow-[0_0_30px_-5px_hsl(var(--warning)/0.35)] hover:border-warning/50",
      icon: "text-warning bg-warning/10",
    },
    purple: {
      border: "border-purple-500/30",
      glow: "shadow-[0_0_20px_-5px_rgba(168,85,247,0.2)] hover:shadow-[0_0_30px_-5px_rgba(168,85,247,0.35)] hover:border-purple-500/50",
      icon: "text-purple-400 bg-purple-500/10",
    },
  };

  // Legacy solid background styles
  const solidStyles = {
    blue: "from-info/20 to-info/10 border-info/20 hover:border-info/40",
    green: "from-success/20 to-success/10 border-success/20 hover:border-success/40",
    red: "from-destructive/20 to-destructive/10 border-destructive/20 hover:border-destructive/40",
    amber: "from-warning/20 to-warning/10 border-warning/20 hover:border-warning/40",
    purple: "from-purple-500/20 to-purple-600/10 border-purple-500/20 hover:border-purple-500/40",
  };

  const style = glowStyles[color] || glowStyles.blue;

  return (
    <motion.div
      whileHover={{ scale: 1.02, y: -2 }}
      transition={{ duration: 0.2 }}
      className={cn(
        "relative overflow-hidden rounded-xl p-5 border transition-all duration-300 cursor-pointer",
        glow
          ? cn("bg-card/30 backdrop-blur-sm", style.border, style.glow)
          : cn("bg-gradient-to-br", solidStyles[color]),
        className
      )}
      onClick={onClick}
    >
      {/* Inner glow effect */}
      {glow && (
        <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-white/[0.02] to-transparent pointer-events-none" />
      )}

      <div className="relative z-10">
        <div className="flex items-center justify-between mb-3">
          <div className={cn("p-2 rounded-lg", style.icon)}>
            {icon}
          </div>
          {trend !== undefined && (
            <span
              className={cn(
                "text-xs font-medium px-2 py-1 rounded-full border",
                trendUp
                  ? "bg-success/10 text-success border-success/20"
                  : "bg-destructive/10 text-destructive border-destructive/20"
              )}
              title={trendLabel || undefined}
            >
              {trendUp ? "↑" : "↓"} {Math.abs(trend)}%
            </span>
          )}
        </div>

        <div className="text-3xl font-bold text-foreground mb-1">{value}</div>
        <div className="text-sm text-muted-foreground">{title}</div>
        {description && (
          <div className="text-xs text-muted-foreground/70 mt-2">{description}</div>
        )}
      </div>
    </motion.div>
  );
};
